import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router';
import { useFormik } from 'formik';
import { useToast } from '../../Hooks/useToast';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import * as Yup from 'yup';

import FromikTextField from '../../Components/Utils/Formik_Texfiled';

import { AddExtraObjectApi, UpdateExtraobjects } from '../../Apis/ExtraObjectes/ExtraObjetcts_Api';
import { fetchGetAllExtraObjectsByidApi } from '../../Redux/ExtraObjects_Slice';

import imageCompression from "browser-image-compression";

import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name ist erforderlich')
    .min(3, 'Name muss mindestens 3 Zeichen lang sein'),
  price: Yup.number()
    .required('Preis ist erforderlich')
    .positive('Preis muss positiv sein')
    .typeError('Preis muss eine Zahl sein')
});

const VisuallyHiddenInput = props => (
  <input style={{ display: 'none' }} {...props} />
);

const AddExtraObjectForm = () => {
  const { toastSuccess, toastError } = useToast();
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { data, loading } = useSelector(state => state.ExtraObjects);

  useEffect(() => {
    if (params.id) {
      dispatch(fetchGetAllExtraObjectsByidApi(params.id));
    }
  }, [params.id]);

  useEffect(() => {
    if (params.id && data) {
      formik.setValues({
        name: data.name,
        price: data.price
      });
      if (data.photo) {
        setImagePreview(data.photo);
      }
    }
  }, [params.id, data]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
        };

        const compressedFile = await imageCompression(file, options);
        setSelectedImage(compressedFile);
        setImagePreview(URL.createObjectURL(compressedFile));
      } catch (error) {
        toastError("Bildkomprimierung fehlgeschlagen.");
      }
    }
  };

  const formik = useFormik({
    initialValues: { name: '', price: '' },
    validationSchema,
    onSubmit: async values => {
      try {
        let result;

        const formData = new FormData();
        formData.append('name', values.name);
        formData.append('price', values.price);

        if (selectedImage) {
          formData.append('image', selectedImage);
        }

        if (params.id) {
          result = await UpdateExtraobjects(params.id, formData);
        } else {
          result = await AddExtraObjectApi(formData);
        }

        if (result?.status === (params.id ? 200 : 201)) {
          toastSuccess(
            params.id
              ? 'Zusatzobjekt wurde erfolgreich aktualisiert!'
              : 'Zusatzobjekt wurde erfolgreich erstellt!'
          );
          navigate('/ExtraObjects');
        } else {
          toastError(result?.data?.message || 'Fehler beim Speichern!');
        }
      } catch (error) {
        toastError(error.message || 'Ein Fehler ist aufgetreten!');
        console.error('Submission error:', error);
      }
    }
  });

  return (
    <Box sx={{ maxWidth: 700, width: '100%', mx: 'auto', py: 4, px: { xs: 2, sm: 4, md: 6 } }}>
      <Paper elevation={4} sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              height: { xs: 200, md: 250 },
              border: '2px dashed #D6B981',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: 2,
              mb: 2,
              overflow: 'hidden'
            }}
          >
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="preview"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Kein Bild hochgeladen
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            component="label"
            startIcon={<CloudUploadIcon />}
            sx={{
              bgcolor: '#D6B981',
              color: '#fff',
              fontWeight: 500,
              margin: 'auto',
              display: 'flex',
              justifyContent: 'center',
              mx: 4,
              mb: 3,
              '&:hover': { bgcolor: '#b89f67' }
            }}
          >
            Bild hochladen
            <VisuallyHiddenInput
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
            />
          </Button>

          <FromikTextField 
            formik={formik} 
            name="name" 
            label="Name" 
            placeholder="Name eingeben" 
            sx={{ mb: 3 }} 
          />

          <FromikTextField
            formik={formik}
            name="price"
            label="Preis (€)"
            placeholder="Preis eingeben"
            type="number"
            sx={{ mb: 4 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ py: 1.6, fontWeight: 600, borderRadius: 2, bgcolor: '#c4a96f', '&:hover': { bgcolor: '#aa8e55' } }}
          >
            {loading ? <CircularProgress size={24} /> : params.id ? 'Aktualisieren' : 'Hinzufügen'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default AddExtraObjectForm;
